// PDF.js wrapper: rendering, thumbnails and text extraction. Loaded lazily.
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
import type { TextFragment } from '../textLayout';
import { vendor } from './vendor';

type PdfjsModule = typeof import('pdfjs-dist');
let modulePromise: Promise<PdfjsModule> | null = null;

// The "legacy" build is transpiled with polyfills: the modern build relies on
// very recent JavaScript (e.g. Map.prototype.getOrInsertComputed) that many
// browsers in offices and public administration do not have yet.
export function pdfjs(): Promise<PdfjsModule> {
  modulePromise ??= (import('pdfjs-dist/legacy/build/pdf.mjs') as Promise<unknown> as Promise<PdfjsModule>).then((m) => {
    m.GlobalWorkerOptions.workerSrc = `${vendor('pdfjs').base}pdf.worker.min.mjs`;
    return m;
  });
  return modulePromise;
}

export class PdfPasswordError extends Error {
  constructor(public readonly incorrect: boolean) {
    super(incorrect ? 'Incorrect password' : 'Password required');
    this.name = 'PdfPasswordError';
  }
}

export async function openPdf(bytes: Uint8Array, password?: string): Promise<PDFDocumentProxy> {
  const m = await pdfjs();
  const base = vendor('pdfjs').base;
  const task = m.getDocument({
    // PDF.js transfers the buffer to its worker: give it a copy.
    data: bytes.slice(),
    password,
    cMapUrl: `${base}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${base}standard_fonts/`,
    wasmUrl: `${base}wasm/`,
    iccUrl: `${base}iccs/`,
    enableXfa: false,
  });
  try {
    return await task.promise;
  } catch (err) {
    if (err instanceof m.PasswordException) throw new PdfPasswordError(err.code === m.PasswordResponses.INCORRECT_PASSWORD);
    throw err;
  }
}

/** Shared document instances keyed by the byte array they were opened from. */
const cache = new WeakMap<Uint8Array, Promise<PDFDocumentProxy>>();

export function sharedPdf(bytes: Uint8Array): Promise<PDFDocumentProxy> {
  let doc = cache.get(bytes);
  if (!doc) {
    doc = openPdf(bytes);
    cache.set(bytes, doc);
    doc.catch(() => cache.delete(bytes));
  }
  return doc;
}

export async function releasePdf(bytes: Uint8Array): Promise<void> {
  const doc = cache.get(bytes);
  if (!doc) return;
  cache.delete(bytes);
  try {
    await (await doc).loadingTask.destroy();
  } catch {
    // ignore
  }
}

/** Largest canvas area that every major browser accepts. */
const MAX_PIXELS = 16_000_000;
const MAX_SIDE = 8192;

export interface RenderOptions {
  /** Pixels per PDF point (1 = 72 dpi). */
  scale: number;
  /** Extra clockwise rotation on top of the page's own /Rotate. */
  rotation?: number;
  background?: string;
}

export async function renderPage(doc: PDFDocumentProxy, pageIndex: number, opts: RenderOptions): Promise<HTMLCanvasElement> {
  const page: PDFPageProxy = await doc.getPage(pageIndex + 1);
  const rotation = (((page.rotate + (opts.rotation ?? 0)) % 360) + 360) % 360;
  let viewport = page.getViewport({ scale: opts.scale, rotation });
  const area = viewport.width * viewport.height;
  const limit = Math.min(1, Math.sqrt(MAX_PIXELS / area), MAX_SIDE / Math.max(viewport.width, viewport.height));
  if (limit < 1) viewport = page.getViewport({ scale: opts.scale * limit, rotation });

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const m = await pdfjs();
  await page.render({ canvas, viewport, background: opts.background ?? '#ffffff', annotationMode: m.AnnotationMode.ENABLE_FORMS }).promise;
  page.cleanup();
  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/png', quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas encoding failed'))), type, quality));
}

/** Small JPEG preview of a page as an object URL (caller revokes it). */
export async function thumbnail(doc: PDFDocumentProxy, pageIndex: number, maxSide = 220): Promise<{ url: string; width: number; height: number }> {
  const page = await doc.getPage(pageIndex + 1);
  const base = page.getViewport({ scale: 1 });
  const scale = (maxSide * Math.min(2, window.devicePixelRatio || 1)) / Math.max(base.width, base.height);
  const canvas = await renderPage(doc, pageIndex, { scale });
  const blob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
  const result = { url: URL.createObjectURL(blob), width: canvas.width, height: canvas.height };
  canvas.width = canvas.height = 0;
  return result;
}

export interface PageText {
  fragments: TextFragment[];
  width: number;
  height: number;
}

export async function pageText(doc: PDFDocumentProxy, pageIndex: number): Promise<PageText> {
  const page = await doc.getPage(pageIndex + 1);
  const viewport = page.getViewport({ scale: 1 });
  const content = await page.getTextContent();
  const fragments: TextFragment[] = [];
  for (const item of content.items) {
    if (!('str' in item)) continue;
    const it = item as TextItem;
    const [a, b, c, d, e, f] = it.transform as number[];
    const size = Math.hypot(c, d) || Math.hypot(a, b) || it.height;
    fragments.push({ str: it.str, x: e, y: f, width: it.width, size });
  }
  page.cleanup();
  return { fragments, width: viewport.width, height: viewport.height };
}

/** Whether a page already contains real text (used to skip OCR). */
export async function pageHasText(doc: PDFDocumentProxy, pageIndex: number): Promise<boolean> {
  const { fragments } = await pageText(doc, pageIndex);
  return fragments.reduce((n, f) => n + f.str.trim().length, 0) > 20;
}
